import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, Calendar, Users, ExternalLink, ChevronDown, ChevronRight,
  AlertTriangle, Link2, Lightbulb, Bell, X, CheckCircle, Activity
} from 'lucide-react';
import PageLayout from '@/components/layout/PageLayout';

const API = process.env.REACT_APP_BACKEND_URL;
const CATEGORY_LABELS = {
  einvoicing: 'e-Invoicing',
  saft: 'SAF-T & Digital Audit',
  vat_returns: 'VAT Returns & Reporting',
  vat_structure: 'VAT Structure',
};

/* ─── Track This Modal ─── */
const TrackThisModal = ({ country, category, onClose, onSuccess }) => {
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !email.trim()) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/tracker/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ first_name: firstName.trim(), email: email.trim().toLowerCase(), country_code: country, category })
      });
      if (!res.ok) throw new Error('Failed to subscribe');
      const data = await res.json();
      sessionStorage.setItem('tracker_token', data.token);
      setDone(true);
      setTimeout(() => { onSuccess?.(data); onClose(); }, 2000);
    } catch (err) {
      setError('Something went wrong. Please try again.');
    }
    setLoading(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 10 }}
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 relative"
        onClick={e => e.stopPropagation()}
        data-testid="track-this-modal"
      >
        <button onClick={onClose} className="absolute top-4 right-4 p-1 text-slate-400 hover:text-slate-600" data-testid="track-modal-close">
          <X className="w-4 h-4" />
        </button>

        {done ? (
          <div className="text-center py-4">
            <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-3" />
            <h3 className="font-heading text-lg font-bold text-taxera-charcoal mb-1">You're tracking this</h3>
            <p className="text-sm text-slate-500">Check your email for your personal tracker access link.</p>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-1">
              <Bell className="w-4 h-4 text-taxera-teal" />
              <h3 className="font-heading text-lg font-bold text-taxera-charcoal">Track this country</h3>
            </div>
            <p className="text-sm text-slate-500 mb-5">
              Get notified when something changes or when new practical insights are added. No password needed — we'll send you a personal access link.
            </p>
            <form onSubmit={handleSubmit} className="space-y-3">
              <input
                type="text"
                value={firstName}
                onChange={e => setFirstName(e.target.value)}
                placeholder="First name"
                required
                className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-taxera-teal focus:ring-1 focus:ring-taxera-teal/20"
                data-testid="track-input-name"
              />
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="Email address"
                required
                className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-taxera-teal focus:ring-1 focus:ring-taxera-teal/20"
                data-testid="track-input-email"
              />
              {error && <p className="text-xs text-red-500">{error}</p>}
              <button
                type="submit"
                disabled={loading}
                className="w-full py-2.5 rounded-xl bg-taxera-blue text-white text-sm font-bold hover:bg-taxera-blue/90 disabled:opacity-50 transition-colors"
                data-testid="track-submit-btn"
              >
                {loading ? 'Setting up...' : 'Track this'}
              </button>
            </form>
            <p className="text-2xs text-slate-400 mt-3 text-center">
              You can unsubscribe or delete your data at any time. We respect your privacy.
            </p>
          </>
        )}
      </motion.div>
    </motion.div>
  );
};

/* ─── Collapsible Layer Section ─── */
const LayerSection = ({ title, accent, icon: Icon, children, defaultOpen = false, testId }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden" data-testid={testId}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-4 lg:p-5 bg-white hover:bg-slate-50/50 transition-colors text-left"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: accent + '15' }}>
            <Icon className="w-4 h-4" style={{ color: accent }} />
          </div>
          <h3 className="font-heading text-sm lg:text-base font-bold text-taxera-charcoal">{title}</h3>
        </div>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-5 lg:px-5 lg:pb-6 pt-0">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ─── Main Page ─── */
export default function TrackerCountryPage() {
  const { categoryId = 'einvoicing', countryCode } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTrackModal, setShowTrackModal] = useState(false);
  const token = sessionStorage.getItem('tracker_token');

  // eslint-disable-next-line react-hooks/exhaustive-deps -- API is a module constant
  useEffect(() => {
    if (!countryCode) return;
    setLoading(true);
    fetch(`${API}/api/tracker/content/${countryCode.toUpperCase()}/${categoryId}`)
      .then(r => { if (!r.ok) throw new Error('Not found'); return r.json(); })
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [categoryId, countryCode]);

  if (loading) return <PageLayout><div className="min-h-screen flex items-center justify-center text-slate-400">Loading...</div></PageLayout>;
  if (!data) return <PageLayout><div className="min-h-screen flex items-center justify-center text-slate-400">Country not found</div></PageLayout>;

  const { layer1, layer2, layer3 } = data;

  return (
    <PageLayout>
      <div className="min-h-screen bg-white" data-testid="tracker-country-page">

        {/* Breadcrumb + header */}
        <section className="pt-8 pb-6 border-b border-slate-100">
          <div className="max-w-4xl mx-auto px-6">
            <div className="flex items-center gap-2 text-xs text-slate-400 mb-4">
              <Link to="/tracker" className="hover:text-taxera-blue transition-colors">Tracker</Link>
              <ChevronRight className="w-3 h-3" />
              <Link to={`/tracker/${categoryId}`} className="hover:text-taxera-blue transition-colors">{CATEGORY_LABELS[categoryId] || categoryId}</Link>
              <ChevronRight className="w-3 h-3" />
              <span className="text-taxera-charcoal font-semibold">{data.country_name}</span>
            </div>

            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-4">
                <img
                  src={`https://flagcdn.com/48x36/${data.country_code.toLowerCase()}.png`}
                  alt={data.country_name}
                  className="w-12 h-9 rounded-md object-cover border border-slate-200"
                />
                <div>
                  <h1 className="font-heading text-2xl lg:text-3xl font-bold text-taxera-charcoal">{data.country_name}</h1>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-2xs font-bold px-2.5 py-0.5 rounded-full text-white" style={{ backgroundColor: data.status_color }}>
                      {data.status_label}
                    </span>
                    <span className="text-xs text-slate-400 flex items-center gap-1">
                      <Activity className="w-3 h-3" /> Updated {data.last_updated}
                    </span>
                    {data.changes_this_quarter > 0 && (
                      <span className="text-2xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full font-semibold">
                        {data.changes_this_quarter} change{data.changes_this_quarter > 1 ? 's' : ''} this quarter
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <button
                onClick={() => setShowTrackModal(true)}
                className="shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-xl bg-taxera-blue text-white text-sm font-bold hover:bg-taxera-blue/90 transition-colors shadow-sm"
                data-testid="track-this-btn"
              >
                <Bell className="w-4 h-4" /> Track this
              </button>
            </div>
          </div>
        </section>

        {/* Content layers */}
        <section className="py-8">
          <div className="max-w-4xl mx-auto px-6 space-y-4">

            {/* Layer 1: Baseline — always open */}
            <div className="bg-white rounded-xl border border-slate-200 p-5 lg:p-6" data-testid="layer-1-baseline">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-2 h-2 rounded-full bg-green-500" />
                <span className="text-2xs font-bold text-slate-400 uppercase tracking-wide">Baseline</span>
              </div>

              <h2 className="font-heading text-lg font-bold text-taxera-charcoal mb-3">{layer1.mandate_name}</h2>
              <p className="text-sm text-slate-600 leading-relaxed mb-5">{layer1.summary}</p>

              {/* Key dates */}
              {layer1.key_dates?.length > 0 && (
                <div className="mb-5">
                  <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5" /> Key Dates
                  </h4>
                  <div className="space-y-2">
                    {layer1.key_dates.map((d) => (
                      <div key={d.date} className="flex items-start gap-3 text-sm">
                        <span className="text-xs font-bold text-taxera-blue bg-taxera-blue/5 px-2 py-0.5 rounded shrink-0 mt-0.5">{d.date}</span>
                        <span className="text-slate-600">{d.event}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* In scope */}
              {layer1.in_scope && (
                <div className="mb-5">
                  <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5 flex items-center gap-1.5">
                    <Users className="w-3.5 h-3.5" /> Who is in scope
                  </h4>
                  <p className="text-sm text-slate-600">{layer1.in_scope}</p>
                </div>
              )}

              {/* Process */}
              {layer1.process && (
                <div className="mb-5">
                  <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Process</h4>
                  <p className="text-sm text-slate-600 leading-relaxed">{layer1.process}</p>
                </div>
              )}

              {/* Official sources */}
              {layer1.official_sources?.length > 0 && (
                <div>
                  <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                    <ExternalLink className="w-3.5 h-3.5" /> Official Sources
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {layer1.official_sources.map((s, i) => (
                      <a key={`source-${s.url || i}`} href={s.url} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-taxera-blue bg-taxera-blue/5 px-3 py-1.5 rounded-full hover:bg-taxera-blue/10 transition-colors">
                        <ExternalLink className="w-3 h-3" /> {s.label}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Layer 2: Business Process — collapsible */}
            {layer2 && (
              <LayerSection title="Business Process Impact" accent="#0066CC" icon={Link2} testId="layer-2-process">
                {layer2.business_process && (
                  <div className="mb-4">
                    <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">What it means operationally</h4>
                    <p className="text-sm text-slate-600 leading-relaxed">{layer2.business_process}</p>
                  </div>
                )}
                {layer2.system_implications && (
                  <div className="mb-4">
                    <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">System implications</h4>
                    <p className="text-sm text-slate-600 leading-relaxed">{layer2.system_implications}</p>
                  </div>
                )}
                {layer2.data_flow && (
                  <div className="bg-slate-50 rounded-xl p-4">
                    <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Data flow</h4>
                    <p className="text-xs text-slate-600 font-mono leading-relaxed">{layer2.data_flow}</p>
                  </div>
                )}
              </LayerSection>
            )}

            {/* Layer 3: Practical Insights — collapsible */}
            {layer3 && (
              <LayerSection title="Practical Insights & What to Watch" accent="#f59e0b" icon={Lightbulb} testId="layer-3-insights">
                {layer3.practical_insights?.length > 0 && (
                  <div className="mb-5">
                    <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3">Key insights</h4>
                    <div className="space-y-3">
                      {layer3.practical_insights.map((insight, i) => (
                        <div key={`insight-${i}-${insight.slice(0, 20)}`} className="flex items-start gap-3">
                          <Lightbulb className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                          <p className="text-sm text-slate-600 leading-relaxed">{insight}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {layer3.common_pitfalls?.length > 0 && (
                  <div className="mb-5">
                    <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                      <AlertTriangle className="w-3.5 h-3.5 text-red-400" /> Common pitfalls
                    </h4>
                    <div className="space-y-2">
                      {layer3.common_pitfalls.map((pitfall, i) => (
                        <div key={`pitfall-${i}-${pitfall.slice(0, 20)}`} className="flex items-start gap-3 bg-red-50/50 rounded-lg p-3">
                          <AlertTriangle className="w-3.5 h-3.5 text-red-400 shrink-0 mt-0.5" />
                          <p className="text-sm text-slate-600">{pitfall}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {layer3.dependencies?.length > 0 && (
                  <div>
                    <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                      <Link2 className="w-3.5 h-3.5 text-indigo-400" /> Dependencies & cross-mandate links
                    </h4>
                    <div className="space-y-2">
                      {layer3.dependencies.map((dep, i) => (
                        <div key={`dep-${i}-${dep.slice(0, 20)}`} className="flex items-start gap-3 bg-indigo-50/50 rounded-lg p-3">
                          <Link2 className="w-3.5 h-3.5 text-indigo-400 shrink-0 mt-0.5" />
                          <p className="text-sm text-slate-600">{dep}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </LayerSection>
            )}

            {/* Inline CTA */}
            <div className="bg-slate-50 rounded-xl border border-slate-200 p-5 text-center" data-testid="inline-track-cta">
              <Bell className="w-5 h-5 text-taxera-teal mx-auto mb-2" />
              <h4 className="font-heading text-sm font-bold text-taxera-charcoal mb-1">Stay informed on {data.country_name}</h4>
              <p className="text-xs text-slate-500 mb-3">Get notified when mandates change or new practical insights are added.</p>
              <button
                onClick={() => setShowTrackModal(true)}
                className="px-5 py-2 rounded-xl bg-taxera-blue text-white text-sm font-bold hover:bg-taxera-blue/90 transition-colors"
                data-testid="inline-track-btn"
              >
                Track this country
              </button>
            </div>

          </div>
        </section>

        {/* Track This Modal */}
        <AnimatePresence>
          {showTrackModal && (
            <TrackThisModal
              country={data.country_code}
              category={categoryId}
              onClose={() => setShowTrackModal(false)}
              onSuccess={() => {}}
            />
          )}
        </AnimatePresence>
      </div>
    </PageLayout>
  );
}
